import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo } from "react";
import { FormikContext } from "formik";
import { useMask } from "../../../../hooks";
import Text from "../../Text";
import ControlTemplate, {
  ControlContent,
  ControlContainer,
  ErrorMsg,
} from "./Control.Style";
import { applyKey, getOnlyNumbers } from "../../../../utils";

const getValueOrNumbers = (onlyNumbers, value) =>
  onlyNumbers ? getOnlyNumbers(value) : value;

const Control = (props) => {
  const { className, label, name, onChange, mask, valueNumbers } = props;
  const { values, setFieldValue, errors, touched } = useContext(FormikContext);

  const { applyMask } = useMask(mask);

  const controlErrors = useMemo(
    () => (touched[name] && errors[name] ? [].concat(errors[name]) : null),
    [errors, name, touched]
  );

  const rawValue = useMemo(() => {
    const v = values[name] || "";
    return getValueOrNumbers(valueNumbers, v);
  }, [name, valueNumbers, values]);

  const value = useMemo(() => {
    if (!mask) {
      return rawValue;
    }

    const { inputValue, dataValue } = applyMask(rawValue);

    return dataValue ? inputValue : "";
  }, [applyMask, mask, rawValue]);

  const handleChange = useCallback(
    (evt) => {
      const dataValue = getValueOrNumbers(
        valueNumbers,
        evt.currentTarget.value
      );

      setFieldValue(name, dataValue, true);
      if (onChange) {
        onChange(evt);
      }
    },
    [name, onChange, setFieldValue, valueNumbers]
  );

  const maskHandleChange = useCallback(
    (evt) => {
      const { dataValue } = applyMask(
        getValueOrNumbers(valueNumbers, evt.currentTarget.value)
      );

      setFieldValue(name, dataValue, true);

      if (onChange) {
        onChange(evt);
      }
    },
    [applyMask, name, onChange, setFieldValue, valueNumbers]
  );

  const controlProps = { ...props };

  delete controlProps.valueNumbers;

  return (
    <ControlContainer className={`FormControl ${className || ""}`}>
      <Text textSize="sm" as="label">
        {label}
        <ControlContent>
          <ControlTemplate
            {...controlProps}
            onChange={mask ? maskHandleChange : handleChange}
            value={value}
          />
        </ControlContent>
      </Text>
      {controlErrors
        ? applyKey(controlErrors, (error) => (
            <Text as={ErrorMsg} textType="caption" text={error} />
          ))
        : null}
    </ControlContainer>
  );
};

Control.defaultProps = {
  label: null,
  placeholder: null,
  onChange: null,
  mask: null,
  valueNumbers: false,
};

Control.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  mask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      patterIn: PropTypes.string.isRequired,
      patterOut: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
    }),
  ]),
  valueNumbers: PropTypes.bool,
};

export default Control;
